import { BrowserSupport } from '@tableau/browser-support';
import React from 'react';
import { DragSourceMonitor, useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';

import { ToasterService } from '~commonInfra/services/ToasterService';

import { DraftsById } from '../../../drafts/types/DraftsTypes';
import { ConnectedSharedWithMeCard } from '../../../sharing/components/cardView/SharedWithMeCard';
import { getExplorableItemDefinition } from '../../contentDefinitionInterfaces/ContentDefinitionsLists';
import { getKeyForExplorableItem } from '../../models/ContentType';
import { IContentInfo } from '../../models/IContentInfo';
import { IFavoritableItemInfo } from '../../models/resourceInfoParts';
import { SpaceInfo } from '../../state/SpaceInfo';
import { Hover } from '../Explorer/widgets/Hover';
import { SpacesSiteUrls } from '../spaceComponents/SpacesSiteUrls';
import { CardViewStyles } from './CardViewStyles';
import { ContentCardProps } from './ContentCard';
import { ContentCardSelectCheckbox } from './ContentCardSelectCheckbox';
import { CardItemType, DraggableItemType } from './DraggableItemTypes';
import { GenericCard } from './GenericCard';

export interface ContentCardWrapperStateProps {
  readonly urls: SpacesSiteUrls;
  readonly inWorkbookDatasourcesSubspace: boolean;
  readonly inDatasourceConnectionsSubspace: boolean;
  readonly inProjectSpace: boolean;
  readonly inRecentsSpace: boolean;
  readonly spaceInfo: SpaceInfo;
  readonly drafts?: DraftsById;
}

export interface ContentCardWrapperDispatchProps {
  readonly addContentFavorite: (item: IFavoritableItemInfo) => any;
  readonly removeContentFavorite: (item: IFavoritableItemInfo) => any;
  readonly getContentDetail: (content: IContentInfo) => void;
  readonly showLastAccessedAt?: boolean;
  readonly reorderItem: (fromIndex: number, toIndex: number, spaceInfo: SpaceInfo) => void;
}

export interface ContentCardWrapperOwnProps {
  readonly content: IContentInfo;
  readonly itemIndex?: number;
  readonly row?: number;
  readonly showActionMenu: boolean;
  readonly showLastAccessedAt?: boolean;
  readonly selected?: boolean;
  readonly toggleSelection?: (content: IContentInfo) => void;
  readonly forDragPreview: boolean;
  readonly dragInProgress: boolean;
  readonly setIsDragging: (isDragging: boolean) => void;

  readonly keyboardReorderInProgress?: boolean;
  readonly setCurrentlyFocusedCardInfo?: (startIndex: number, ref: any) => void;
  readonly reordering_ariaText?: string;
  readonly reorderItemAction: (fromIndex: number, toIndex: number, spaceInfo: SpaceInfo) => void;
  readonly allowDragAndDrop: boolean;
}

export type ContentCardWrapperProps = ContentCardWrapperOwnProps
  & ContentCardWrapperStateProps
  & ContentCardWrapperDispatchProps;

export const ContentCardWrapper: React.FC<ContentCardWrapperProps> = props => {
  const ref = React.useRef(null);
  const item: CardItemType = {
    type: DraggableItemType.Card,
    index: props.itemIndex,
    row: props.row ?? 0,
    content: props.content,
  };

  const [ { dragOpacity }, drag, preview ] = useDrag({
    item,
    begin: () => {
      return handleBeginDrag(item, props);
    },
    collect: monitor => ({
      dragOpacity: monitor.isDragging() ? 0 : 1,
    }),
    end: (droppedItem: CardItemType, monitor: DragSourceMonitor) => {
      handleEndDrag(droppedItem, monitor, props);
    }
  });

  const [ { dropOpacity }, drop ] = useDrop({
    accept: 'card',
    collect: monitor => ({
      dropOpacity: monitor.isOver() ? 0.5 : 1,
    }),
    hover: (draggedItem: CardItemType) => {
      if (!ref.current) return;
      handleHover(draggedItem, item, props);
    },
    drop: (droppedItem: CardItemType) => { return droppedItem; }
  });

  React.useEffect(
    () => {
      preview(getEmptyImage(), { captureDraggingState: true });
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [ isHovered, setIsHovered ] = React.useState(false);

  const testId = `${props.content.contentType}-${props.content.id}-card`;

  // optional chainining and giving null to isEqualNode doesn't work in IE11 so have
  // to check all of these things first
  const isActiveElement = !!ref && !!ref.current && !!document && !!document.activeElement?
    ref.current.isEqualNode(document.activeElement) : false;

  // Make sure the checkbox is absolutely positioned with respect to the proper bounds
  const CardWrapperStyle: React.CSSProperties = {
    position: 'relative',
    opacity: Math.min(dragOpacity, dropOpacity),
  };

  const AdditionalCardWrapperStyle = props.keyboardReorderInProgress && isActiveElement? {
      boxShadow: '0 8px 12px 4px rgba(0, 0, 0, 0.15)',
    } : {};

  const handleDivRef = (divRef: HTMLDivElement | null) => {
    ref.current = divRef;
    if (ref.current && props.allowDragAndDrop) {
      drag(drop(ref));
    }
  };

  const DisablingStylesForReorder: React.CSSProperties = {
    WebkitTouchCallout: 'none', // disable iOS link popup
    // disable text selection
    userSelect: 'none',
    WebkitUserSelect: 'none',
    MozUserSelect: 'none',
    msUserSelect: 'none',
  };

  const innerElement = (
    <Hover groupName="content-card-select" onEnter={() => setIsHovered(true)} onLeave={() => setIsHovered(false)}>
      {React.cloneElement(React.Children.only(renderInnerCard(props, props.dragInProgress || props.keyboardReorderInProgress)), { hovered: isHovered, selected: props.selected })}
        <ContentCardSelectCheckbox {...props}/>
    </Hover>
  );

  /* eslint-disable jsx-a11y/no-noninteractive-tabindex */
  return (props.allowDragAndDrop?
    <>
      { props.reordering_ariaText && isActiveElement &&  // only put text on active element so it doesn't read out for every card
        <div aria-live={'assertive'} role={'status'} className={'sr-only'}>{props.reordering_ariaText}</div>}
      <div
        data-tb-test-id={testId}
        style={{...CardWrapperStyle, ...AdditionalCardWrapperStyle, ...DisablingStylesForReorder}}
        className={CardViewStyles.CardWrapperFocusClass}
        ref={handleDivRef}
        onFocus={() => {
          props.setCurrentlyFocusedCardInfo(props.itemIndex, ref.current);
        }}
        onBlur={() => {
          props.setCurrentlyFocusedCardInfo(undefined, undefined);
        }}
        onTouchStart={handleTouchStart}
        tabIndex={0}
        >
        {innerElement}
      </div>
    </>
    :
    <div
      data-tb-test-id={testId}
      style={CardWrapperStyle}
      ref={ref}>
      {innerElement}
    </div>
  );
};
ContentCardWrapper.displayName = 'ContentCardWrapper';

/**
 * Prevent drag and drop reordering in IE11 and Edge.
 * `react-dnd-touch-backend` causes forced reload on IE11
 * and behaves unreliably in legacy (non Chromium-based) Edge
 *
 * Note: legacy Edge does not support touch events by default,
 * can only be enabled as a user setting so will not prevent
 * reordering unless user enables touch events
 */
const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
  if(BrowserSupport.IsIE() || BrowserSupport.IsLegacyEdge()) {
    e.stopPropagation();
    return;
  }
};

export const handleBeginDrag = (item: CardItemType, props: ContentCardWrapperProps) => {
  props.setIsDragging(true);
  return {
    ...item,
    startIndex: item.index,
  };
};

export const handleEndDrag = (droppedItem: CardItemType, monitor: DragSourceMonitor, props: ContentCardWrapperProps) => {
  removeToastsAfterReordering();

  const didCompleteDrop = monitor.didDrop();  // this is false if dropped outside drop target or ESC while dragging
  if (!didCompleteDrop) {
    if (droppedItem.startIndex < droppedItem.index) {
      props.reorderItem(droppedItem.index, droppedItem.startIndex - 1, props.spaceInfo);
    } else {
      props.reorderItem(droppedItem.index, droppedItem.startIndex, props.spaceInfo);
    }
  } else if (droppedItem.startIndex !== droppedItem.index) {
    props.reorderItemAction(droppedItem.startIndex, droppedItem.index - 1, props.spaceInfo);
  }
  props.setIsDragging(false);
};

export const handleHover = (draggedItem: CardItemType, item: CardItemType, props: ContentCardWrapperProps) => {
  if(draggedItem.index === item.index) return;

  const hoverRegion = targetCardRegion(draggedItem, item);

  if ((hoverRegion === 'right' || hoverRegion === 'bottom') && draggedItem.index !== item.index - 1) {
    props.reorderItem(draggedItem.index, item.index - 1, props.spaceInfo);
    draggedItem.index = item.index;
    draggedItem.row = item.row;
  } else if ((hoverRegion === 'left' || hoverRegion === 'top') && draggedItem.index !== item.index + 1) {
    props.reorderItem(draggedItem.index, item.index, props.spaceInfo);
    draggedItem.index = item.index;
    draggedItem.row = item.row;
  }
};

type CardRegion = 'left' | 'right' | 'top' | 'bottom';

function removeToastsAfterReordering() {
  // Undoing un-favoriting an item from a toast doesn't work after reordering favorites because
  // the favorited item is in the wrong place. We also can't simply refresh the whole page when
  // undoing un-favoriting because the SOLR Favorites Core is slow and so we can't show the updates
  // fast enough. Instead, just remove the toasts after reordering.
  const toasterService = ToasterService.getInstance();
  toasterService.closeAllToastsNotMarkedAsKeep();
}

function targetCardRegion(draggedCard: CardItemType, targetCard: CardItemType): CardRegion {
  if (draggedCard.row < targetCard.row) {
    return 'top';
  } else if (draggedCard.row > targetCard.row) {
    return 'bottom';
  } else if (draggedCard.index < targetCard.index) {
    return 'left';
  } else {
    return 'right';
  }
}

function renderInnerCard(wrapperProps: ContentCardWrapperProps, disableTooltip: boolean): JSX.Element {
  const cardProps: React.Attributes & ContentCardProps = {
    ...wrapperProps,
    key: getKeyForExplorableItem(wrapperProps.content),
    selected: wrapperProps.selected,
  };

  const contentDefinition = getExplorableItemDefinition(wrapperProps.content.contentType);
  let Card;
  if (wrapperProps.content.hasOwnProperty('sharer')) {
    Card = ConnectedSharedWithMeCard;
  } else if (contentDefinition) {
    Card = contentDefinition.cardComponent;
  } else {
    Card = GenericCard;
  }

  if (Card) {
    return <Card {...cardProps}/>;
  } else {
    // No card component exists for some content types (e.g. flow output).
    return null;
  }
}
